import React, { useState, useEffect } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Check, Zap } from "lucide-react";
import type { ChecklistNode as ChecklistNodeType, PatientInput } from "@/lib/pharmacyFirst/types";
import { calculateAgeYearsFromDateOfBirth } from "@/lib/pharmacyFirst/utils/ageCalculator";
import { ClinicalLinkChips } from "./SingleSelectNode";
import { SepsisWarningPanel } from "./SepsisWarningPanel";
import { ImageCarousel } from "./ImageCarousel";

interface ChecklistNodeProps {
  node: ChecklistNodeType;
  isDarkMode?: boolean;
  onAnswer: (selectedIds: string[]) => void;
  value?: string[];
  stepNumber?: number;
  patient?: PatientInput;
}

export function ChecklistNode({
  node,
  isDarkMode = false,
  onAnswer,
  value = [],
  stepNumber,
  patient,
}: ChecklistNodeProps) {
  const [selected, setSelected] = useState<string[]>(() => {
    // Auto-select items based on patient age
    if (patient?.dateOfBirth) {
      const ageYears = calculateAgeYearsFromDateOfBirth(patient.dateOfBirth);
      if (ageYears !== null) {
        const autoSelected = node.items
          .filter((item) => item.autoSelectIfAgeAtLeast !== undefined && ageYears >= item.autoSelectIfAgeAtLeast)
          .map((item) => item.id);
        return [...new Set([...value, ...autoSelected])];
      }
    }
    return value;
  });
  const [noneChecked, setNoneChecked] = useState<boolean>(false);
  const [prefilledIds] = useState<Set<string>>(() => {
    if (patient?.dateOfBirth) {
      const ageYears = calculateAgeYearsFromDateOfBirth(patient.dateOfBirth);
      if (ageYears !== null) {
        return new Set(
          node.items
            .filter((item) => item.autoSelectIfAgeAtLeast !== undefined && ageYears >= item.autoSelectIfAgeAtLeast)
            .map((item) => item.id)
        );
      }
    }
    return new Set();
  });

  useEffect(() => {
    // Only reset when moving to a new node, not when parent value changes
    setNoneChecked(false);
  }, [node.id]);

  const isConfirmation = node.variant === "confirmation";

  const handleToggle = (itemId: string) => {
    setNoneChecked(false);
    setSelected((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId],
    );
  };

  const handleNoneToggle = () => {
    if (noneChecked) {
      setNoneChecked(false);
    } else {
      setNoneChecked(true);
      setSelected([]);
    }
  };

  const canContinue = isConfirmation
    ? node.includeNoneOption
      ? selected.length > 0 || noneChecked
      : selected.length > 0
    : node.includeNoneOption
      ? selected.length > 0 || noneChecked
      : true;

  const heading = isConfirmation
    ? node.confirmationHeading || node.text
    : node.text;
  const subText = isConfirmation
    ? "Tick any that apply — at least one is required to proceed."
    : "Select all that apply";

  const itemSelectedClass = isConfirmation
    ? isDarkMode
      ? "border-emerald-500 bg-emerald-950/40"
      : "border-emerald-500 bg-emerald-50"
    : isDarkMode
      ? "border-gray-500 bg-gray-700"
      : "border-gray-300 bg-gray-50";

  return (
    <div
      className={`border rounded-2xl p-4 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      {stepNumber !== undefined && (
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
          <div className="flex-1">
            <h3
              className={`text-sm font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {heading}
            </h3>
            <p
              className={`text-xs mt-1 ${
                isDarkMode ? "text-gray-400" : "text-gray-500"
              }`}
            >
              {subText}
            </p>
            <ClinicalLinkChips
              links={node.clinicalLinks}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      )}

      {stepNumber === undefined && (
        <div className="mb-4">
          <h3
            className={`text-sm font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {heading}
          </h3>
          <p
            className={`text-xs mt-1 ${
              isDarkMode ? "text-gray-400" : "text-gray-500"
            }`}
          >
            {subText}
          </p>
          <ClinicalLinkChips
            links={node.clinicalLinks}
            isDarkMode={isDarkMode}
          />
        </div>
      )}

      {(node.showSepsisWarning || node.id === "q1_sepsis") && (
        <SepsisWarningPanel
          isDarkMode={isDarkMode}
          isChild={patient?.dateOfBirth ? calculateAgeYearsFromDateOfBirth(patient.dateOfBirth) < 16 : false}
        />
      )}

      {node.imageCarousel && (
        <ImageCarousel
          title={node.imageCarousel.title}
          images={node.imageCarousel.images}
          isDarkMode={isDarkMode}
        />
      )}

      <div className="space-y-2">
        {node.items.map((item) => {
          const isSelected = selected.includes(item.id);
          return (
            <label
              key={item.id}
              htmlFor={`${node.id}-${item.id}`}
              className={`flex items-center space-x-2 p-2 border rounded-lg cursor-pointer transition ${
                isSelected
                  ? itemSelectedClass
                  : isDarkMode
                    ? "border-gray-700 hover:bg-gray-700"
                    : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              {isConfirmation ? (
                <span
                  className={`inline-flex items-center justify-center h-4 w-4 rounded border ${
                    isSelected
                      ? "bg-emerald-600 border-emerald-600 text-white"
                      : isDarkMode
                        ? "border-gray-500"
                        : "border-gray-300"
                  }`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleToggle(item.id);
                  }}
                >
                  {isSelected && <Check className="h-3 w-3" />}
                </span>
              ) : (
                <Checkbox
                  id={`${node.id}-${item.id}`}
                  checked={isSelected}
                  onCheckedChange={() => handleToggle(item.id)}
                />
              )}
              <div className="flex-1 flex items-center justify-between">
                <span
                  className={`text-sm cursor-pointer ${
                    isDarkMode ? "text-gray-200" : "text-gray-700"
                  }`}
                  onClick={(e) => {
                    if (isConfirmation) {
                      e.preventDefault();
                      handleToggle(item.id);
                    }
                  }}
                >
                  {item.text}
                </span>
                {prefilledIds.has(item.id) && (
                  <span className={`inline-flex items-center gap-1 ml-2 px-2 py-1 rounded text-xs font-medium ${
                    isDarkMode
                      ? "bg-blue-950 text-blue-200 border border-blue-700"
                      : "bg-blue-100 text-blue-800 border border-blue-300"
                  }`}>
                    <Zap className="h-3 w-3" />
                    Auto
                  </span>
                )}
              </div>
            </label>
          );
        })}

        {node.includeNoneOption && (
          <div className="border-t pt-2 mt-2">
            <div className="flex items-center space-x-2 p-2">
              <Checkbox
                id={`${node.id}-none`}
                checked={noneChecked}
                onCheckedChange={handleNoneToggle}
              />
              <label
                htmlFor={`${node.id}-none`}
                className={`text-sm cursor-pointer font-medium ${
                  isDarkMode ? "text-gray-200" : "text-gray-700"
                }`}
              >
                None of the above
              </label>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <Button
          onClick={() => onAnswer(noneChecked ? [] : selected)}
          disabled={!canContinue}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
